/**
 * Portal file releases — see `PortalFileRelease` in src/types/index.ts.
 *
 * Storage layout: every release writes an immutable copy under
 * `portal-files/{portalId}/released/{streamKind}/{dealId}/v{N}-{slug}.{ext}` so the
 * client URL remains stable even if the source `projectFiles` row is later deleted
 * or the version timeline rewound. The Cloud Function `releaseProjectFileToPortal`
 * is the source of truth for the filename, the storage copy, and the release index.
 * Withdrawals route through `unreleaseProjectFileFromPortal`.
 *
 * Why a Cloud Function and not a client-side write? Storage rules block clients from
 * writing under `projectFiles/...` and Firestore rules block client writes to
 * `clientPortals/{portalId}/portalFileReleases/...`. Routing through a callable also
 * lets us atomically increment `releaseVersionIndex` and refuse releases for invalid
 * stream kinds (`three_d_raw`, `timber_list_csv`, `general`).
 */

import { db } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  deleteField,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  EnhancedDeal,
  PortalDeliverableApproval,
  PortalDesignPhase,
  PortalFileRelease,
  PortalReleaseBundle,
  PortalReleaseTarget,
  PortalReleaseTrailFields,
  ProjectFileStream,
  ProjectFileStreamKind,
} from '../types';
import { structureSetKey } from './structureSelectionLabel';

export { formatPortalReleaseAddedAt, portalReleaseToDate } from './portalReleaseDateFormat';

const SUBCOLLECTION = 'portalFileReleases';
const BUNDLE_SUBCOLLECTION = 'portalReleaseBundles';

/** Stream kinds that are eligible for release to the client portal. */
export type ReleasableStreamKind = Extract<
  ProjectFileStreamKind,
  'two_d_pdf' | 'three_d_html'
>;

export function isReleasableStreamKind(k: ProjectFileStreamKind): k is ReleasableStreamKind {
  return k === 'two_d_pdf' || k === 'three_d_html';
}

/** Default client-facing label per stream kind. Re-applied if the admin clears the field. */
export function defaultDisplayLabelForKind(k: ReleasableStreamKind): string {
  return k === 'two_d_pdf' ? 'Engineered Drawings' : '3D Model';
}

/** Forced filename extension per stream kind (defends against `.htm` vs `.html` typos on upload). */
export function extensionForKind(k: ReleasableStreamKind): 'pdf' | 'html' {
  return k === 'two_d_pdf' ? 'pdf' : 'html';
}

const DISPLAY_LABEL_MAX_LEN = 60;

/** Trim, collapse internal whitespace, clamp; reapply default if empty. */
export function normalizeDisplayLabel(raw: string, kind: ReleasableStreamKind): string {
  const collapsed = (raw || '').replace(/\s+/g, ' ').trim();
  if (!collapsed) return defaultDisplayLabelForKind(kind);
  return collapsed.slice(0, DISPLAY_LABEL_MAX_LEN);
}

/** Strip a leading `#` if the user (or the source data) included one — we add exactly one. */
function stripLeadingHash(s: string): string {
  return (s || '').replace(/^#+/, '').trim();
}

/**
 * Build the human-readable client-facing filename for a release. Same logic runs in
 * the Cloud Function (server-side authoritative) and in the modal preview (client-side
 * for the "what will the client see" UX). Keep both in sync.
 *
 * Convention: `#{projectNumber} {descriptor} - {displayLabel} v{releaseVersionIndex}.{ext}`
 *
 * Fallbacks:
 *  - missing `projectNumber` → drop the `#... ` prefix entirely.
 *  - missing `descriptor` → use `clientLabel` if available; else fall back to the source
 *    filename's basename (without extension).
 *  - if both prefix and suffix collapse to empty (no number, no descriptor / label, no
 *    source name) we still emit `{displayLabel} v{N}.{ext}` so the file is named.
 */
export function buildReleasedFilename(args: {
  projectNumber?: string | null;
  descriptor?: string | null;
  clientLabel?: string | null;
  sourceFileName?: string | null;
  displayLabel: string;
  releaseVersionIndex: number;
  kind: ReleasableStreamKind;
}): string {
  const ext = extensionForKind(args.kind);
  const label = normalizeDisplayLabel(args.displayLabel, args.kind);

  const numRaw = stripLeadingHash((args.projectNumber || '').trim());
  const numPart = numRaw ? `#${numRaw}` : '';

  let descriptorPart = (args.descriptor || '').trim();
  if (!descriptorPart) {
    descriptorPart = (args.clientLabel || '').trim();
  }
  if (!descriptorPart) {
    const src = (args.sourceFileName || '').trim();
    if (src) {
      // basename without extension
      descriptorPart = src.replace(/\.[^./\\]+$/, '');
    }
  }

  const prefix = [numPart, descriptorPart].filter(Boolean).join(' ').trim();
  const head = prefix ? `${prefix} - ${label}` : label;
  const filename = `${head} v${args.releaseVersionIndex}.${ext}`;
  return sanitizeReleasedFilename(filename, ext);
}

/**
 * Filesystem-safe version of {@link buildReleasedFilename}'s output: replaces characters
 * that browsers, OS file systems, or Content-Disposition headers will mangle. Keeps the
 * `.{ext}` suffix intact and hard-caps the body to 200 chars (Storage object names need
 * to fit in path segments alongside the rest of the path we generate).
 */
export function sanitizeReleasedFilename(name: string, ext: 'pdf' | 'html'): string {
  let s = (name || '').replace(/[\r\n\x00-\x1f]/g, '');
  s = s.replace(/[/\\:*?"<>|]/g, '-');
  s = s.replace(/\s+/g, ' ').trim();
  // strip trailing dots — Windows refuses them
  s = s.replace(/\.+$/, '');
  // ensure single `.{ext}` suffix
  const lower = s.toLowerCase();
  const wantSuffix = `.${ext}`;
  if (!lower.endsWith(wantSuffix)) {
    s = `${s}${wantSuffix}`;
  }
  // hard cap (with ext)
  const MAX = 200;
  if (s.length > MAX) {
    const keep = MAX - wantSuffix.length;
    s = `${s.slice(0, keep).trimEnd()}${wantSuffix}`;
  }
  if (!s.replace(wantSuffix, '').trim()) {
    s = `release v1${wantSuffix}`;
  }
  return s;
}

/** Pull the convention inputs from a deal — uses canonical naming fields, falls back gracefully. */
export function buildReleasedFilenameFromDeal(args: {
  deal: Pick<EnhancedDeal, 'projectNumber' | 'clientLabel' | 'descriptor' | 'title'> | null | undefined;
  sourceFileName?: string | null;
  displayLabel: string;
  releaseVersionIndex: number;
  kind: ReleasableStreamKind;
}): string {
  const d = args.deal;
  return buildReleasedFilename({
    projectNumber: d?.projectNumber,
    descriptor: d?.descriptor,
    clientLabel: d?.clientLabel,
    sourceFileName: args.sourceFileName,
    displayLabel: args.displayLabel,
    releaseVersionIndex: args.releaseVersionIndex,
    kind: args.kind,
  });
}

function parsePortalTarget(raw: unknown, streamKind: string): PortalReleaseTarget {
  if (raw === 'concept_elevations' || raw === 'engineered_drawings' || raw === 'model') {
    return raw;
  }
  return streamKind === 'three_d_html' ? 'model' : 'engineered_drawings';
}

function tsToDate(v: unknown): Date | undefined {
  return v && typeof v === 'object' && typeof (v as { toDate?: () => Date }).toDate === 'function'
    ? (v as { toDate: () => Date }).toDate()
    : undefined;
}

function parseStringList(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map(x => String(x ?? ''));
}

function parseTrailApproval(raw: unknown): PortalDeliverableApproval | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const d = raw as Record<string, unknown>;
  return {
    status: d.status === 'approved' ? 'approved' : 'pending',
    approvedAt: tsToDate(d.approvedAt),
    approvedByName: String(d.approvedByName || ''),
    approvedByEmail: typeof d.approvedByEmail === 'string' ? d.approvedByEmail : undefined,
    signature: String(d.signature || ''),
    releaseId: typeof d.releaseId === 'string' ? d.releaseId : undefined,
    bundleId: typeof d.bundleId === 'string' ? d.bundleId : undefined,
    releaseVersionIndex: typeof d.releaseVersionIndex === 'number' ? d.releaseVersionIndex : undefined,
    assetUrl: typeof d.assetUrl === 'string' ? d.assetUrl : undefined,
  };
}

/** Shared trail fields on release + bundle docs (all optional; absent on legacy rows). */
function trailFieldsFromFirestore(d: DocumentData): PortalReleaseTrailFields {
  const out: PortalReleaseTrailFields = {};
  if (d.designPhase === 'concept' || d.designPhase === 'engineering') out.designPhase = d.designPhase;
  const ids = parseStringList(d.structureIds);
  if (ids) {
    out.structureIds = ids.filter(Boolean);
    const names = parseStringList(d.structureNames) ?? [];
    out.structureNames = ids.map((_, i) => names[i] ?? '').filter((_, i) => !!ids[i]);
  }
  if (d.approvalRequested === true || d.approvalRequested === false) {
    out.approvalRequested = d.approvalRequested;
    if (d.approvalRequested === true) out.approvalRequestedAt = tsToDate(d.approvalRequestedAt);
  }
  const approval = parseTrailApproval(d.approval);
  if (approval) out.approval = approval;
  if (typeof d.supersededByReleaseId === 'string' && d.supersededByReleaseId.trim()) {
    out.supersededByReleaseId = d.supersededByReleaseId.trim();
  }
  return out;
}

function fromFirestore(snap: QueryDocumentSnapshot<DocumentData>): PortalFileRelease {
  const d = snap.data();
  const streamKind = (d.streamKind === 'two_d_pdf' ? 'two_d_pdf' : 'three_d_html') as ReleasableStreamKind;
  const releasedAt = (d.releasedAt as { toDate?: () => Date } | undefined)?.toDate?.() ?? new Date();
  return {
    ...trailFieldsFromFirestore(d),
    id: snap.id,
    dealId: String(d.dealId || ''),
    bundleId: typeof d.bundleId === 'string' && d.bundleId ? d.bundleId : undefined,
    streamKind,
    sourceFileId: String(d.sourceFileId || ''),
    sourceStructureReleaseId:
      typeof d.sourceStructureReleaseId === 'string' && d.sourceStructureReleaseId.trim()
        ? d.sourceStructureReleaseId.trim()
        : undefined,
    sourceStreamId: String(d.sourceStreamId || ''),
    sourceStreamTitle: String(d.sourceStreamTitle || ''),
    sourceProjectId: String(d.sourceProjectId || ''),
    versionIndex: typeof d.versionIndex === 'number' ? d.versionIndex : undefined,
    versionLabel: typeof d.versionLabel === 'string' ? d.versionLabel : undefined,
    versionNote: typeof d.versionNote === 'string' ? d.versionNote : undefined,
    displayLabel: String(d.displayLabel || ''),
    releaseVersionIndex: typeof d.releaseVersionIndex === 'number' ? d.releaseVersionIndex : 1,
    name: String(d.name || ''),
    mimeType: String(d.mimeType || 'application/octet-stream'),
    size: typeof d.size === 'number' ? d.size : 0,
    releasedUrl: String(d.releasedUrl || ''),
    releasedStoragePath: String(d.releasedStoragePath || ''),
    releasedAt,
    releasedBy: String(d.releasedBy || ''),
    releasedByName: String(d.releasedByName || ''),
    portalTarget: parsePortalTarget(d.portalTarget, streamKind),
    loomUrl: typeof d.loomUrl === 'string' && d.loomUrl.trim() ? d.loomUrl.trim() : undefined,
  };
}

function bundleFromFirestore(snap: QueryDocumentSnapshot<DocumentData>): PortalReleaseBundle {
  const d = snap.data();
  const streamKind = (d.streamKind === 'two_d_pdf' ? 'two_d_pdf' : 'three_d_html') as ReleasableStreamKind;
  const releasedAt = (d.releasedAt as { toDate?: () => Date } | undefined)?.toDate?.() ?? new Date();
  return {
    ...trailFieldsFromFirestore(d),
    id: snap.id,
    dealId: String(d.dealId || ''),
    streamKind,
    releaseVersionIndex: typeof d.releaseVersionIndex === 'number' ? d.releaseVersionIndex : 1,
    displayLabel: String(d.displayLabel || ''),
    releaseIds: Array.isArray(d.releaseIds) ? d.releaseIds.map(String) : [],
    primaryReleaseId: String(d.primaryReleaseId || ''),
    releasedAt,
    releasedBy: String(d.releasedBy || ''),
    releasedByName: String(d.releasedByName || ''),
    portalTarget: parsePortalTarget(d.portalTarget, streamKind),
  };
}

/** Ordered releases for a bundle (skips missing ids). */
export function resolveBundleReleases(
  bundle: PortalReleaseBundle | undefined,
  releases: PortalFileRelease[],
): PortalFileRelease[] {
  if (!bundle) return [];
  const byId = new Map(releases.filter(r => r.id).map(r => [r.id!, r]));
  return bundle.releaseIds.map(id => byId.get(id)).filter((r): r is PortalFileRelease => !!r);
}

/** Latest-version deliverables — full package when bundled, else single newest release. */
export function currentPackageReleases(
  bundle: PortalReleaseBundle | undefined,
  releases: PortalFileRelease[],
): PortalFileRelease[] {
  const fromBundle = resolveBundleReleases(bundle, releases);
  if (fromBundle.length > 0) return fromBundle;
  const latest = latestPortalRelease(releases);
  return latest ? [latest] : [];
}

export function bundlePortalTarget(bundle: PortalReleaseBundle): PortalReleaseTarget {
  if (bundle.portalTarget) return bundle.portalTarget;
  return bundle.streamKind === 'three_d_html' ? 'model' : 'engineered_drawings';
}

/** Highest `releaseVersionIndex` in a release list (e.g. client View/Download targets). */
export function latestPortalRelease<T extends { releaseVersionIndex?: number }>(
  releases: T[],
): T | null {
  if (releases.length === 0) return null;
  return releases.reduce((best, r) =>
    (r.releaseVersionIndex ?? 0) > (best.releaseVersionIndex ?? 0) ? r : best,
  );
}

export function resolvePortalReleaseBundleId(
  releaseId: string | undefined,
  bundles: PortalReleaseBundle[],
): string | undefined {
  if (!releaseId) return undefined;
  return bundles.find(bundle => bundle.releaseIds.includes(releaseId))?.id;
}

/** Build synthetic single-file bundles from legacy releases that predate bundle docs. */
export function synthesizeBundlesFromReleases(releases: PortalFileRelease[]): PortalReleaseBundle[] {
  const byIndex = new Map<number, PortalFileRelease[]>();
  for (const r of releases) {
    const idx = r.releaseVersionIndex;
    const list = byIndex.get(idx) || [];
    list.push(r);
    byIndex.set(idx, list);
  }
  return Array.from(byIndex.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([idx, rs]) => {
      const sorted = [...rs].sort((a, b) => a.name.localeCompare(b.name));
      const primary = sorted[0]!;
      return {
        dealId: primary.dealId,
        streamKind: primary.streamKind,
        releaseVersionIndex: idx,
        displayLabel: primary.displayLabel,
        releaseIds: sorted.map(r => r.id!).filter(Boolean),
        primaryReleaseId: primary.id || '',
        releasedAt: sorted.reduce(
          (latest, r) => (r.releasedAt > latest ? r.releasedAt : latest),
          sorted[0]!.releasedAt,
        ),
        releasedBy: primary.releasedBy,
        releasedByName: primary.releasedByName,
      };
    });
}

/** Merge persisted bundle docs with synthetic bundles for orphaned release indexes. */
export function mergeBundlesWithReleases(
  bundles: PortalReleaseBundle[],
  releases: PortalFileRelease[],
): PortalReleaseBundle[] {
  if (bundles.length === 0) return synthesizeBundlesFromReleases(releases);
  const covered = new Set(bundles.map(b => b.releaseVersionIndex));
  const orphans = releases.filter(r => !covered.has(r.releaseVersionIndex));
  const synthetic = synthesizeBundlesFromReleases(orphans);
  return [...bundles, ...synthetic].sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex);
}

export function subscribePortalBundlesForDeal(
  portalId: string,
  dealId: string,
  callback: (bundles: PortalReleaseBundle[]) => void,
): () => void {
  const q = query(
    collection(db, 'clientPortals', portalId, BUNDLE_SUBCOLLECTION),
    where('dealId', '==', dealId),
    orderBy('releaseVersionIndex', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => bundleFromFirestore(d as QueryDocumentSnapshot<DocumentData>)));
    },
    err => console.warn('[portalReleaseBundles] subscribe failed', err),
  );
}

/**
 * Subscribe to all releases authored from a specific source project + stream — used by the
 * Project Hub Files tab to show "Released" pills next to released versions and to compute
 * the next `releaseVersionIndex` for the modal's filename preview. Uses a collection-group
 * query so the caller doesn't have to know the parent portalId(s).
 */
export function subscribeStreamReleases(
  sourceProjectId: string,
  sourceStreamId: string,
  callback: (releases: PortalFileRelease[]) => void,
): () => void {
  // Callers (ProjectFiles) must skip for designers/engineers/architects — rules
  // deny collection-group reads for those roles. Empty callback keeps UI safe.
  const q = query(
    collectionGroup(db, SUBCOLLECTION),
    where('sourceProjectId', '==', sourceProjectId),
    where('sourceStreamId', '==', sourceStreamId),
    orderBy('releaseVersionIndex', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore(d)));
    },
    err => {
      console.warn('[portalFileReleases] subscribe failed', err);
      callback([]);
    },
  );
}

/**
 * Subscribe to all releases for a single portal scoped to one project (dealId on the
 * multi-project portal). Used by `ClientPortalSplash` to render the version-history
 * popover under the 3D Model and Engineered Drawings step actions.
 */
export function subscribePortalReleasesForDeal(
  portalId: string,
  dealId: string,
  callback: (releases: PortalFileRelease[]) => void,
): () => void {
  const q = query(
    collection(db, 'clientPortals', portalId, SUBCOLLECTION),
    where('dealId', '==', dealId),
    orderBy('releaseVersionIndex', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore(d as QueryDocumentSnapshot<DocumentData>)));
    },
    err => console.warn('[portalFileReleases] subscribe failed', err),
  );
}

export type ReleaseProjectFileToPortalInput = {
  portalId: string;
  dealId: string;
  /** Legacy single-file release. */
  projectFileId?: string;
  /** Multi-file release (min 1). When set, takes precedence over `projectFileId`. */
  projectFileIds?: string[];
  /**
   * Optional override; defaults applied server-side when missing/blank
   * (`Engineered Drawings` for `two_d_pdf`, `3D Model` for `three_d_html`).
   */
  displayLabel?: string;
  /** Package-level label when releasing multiple files together. */
  bundleLabel?: string;
  /** Which file drives the portal pointer (`modelUrl` / `plansUrl`). Defaults to first file. */
  primaryProjectFileId?: string;
  /** Per-file display labels keyed by `projectFiles` doc id. */
  fileDisplayLabels?: Record<string, string>;
  /** Where this release appears in the client portal. */
  portalTarget?: PortalReleaseTarget;
  /** Design phase on the approval trail. Server derives from target / LOD when omitted. */
  designPhase?: PortalDesignPhase;
  /** Ask the client to approve this release (shows as awaiting on the approval trail). */
  requestApproval?: boolean;
};

export type ReleaseProjectFileToPortalResult = {
  releaseId: string;
  releaseIds: string[];
  bundleId: string;
  primaryReleaseId: string;
  releasedUrl: string;
  releasedStoragePath: string;
  name: string;
  releaseVersionIndex: number;
};

/**
 * Locate the `clientPortals` doc that owns a given dealId. Tries (in order):
 * 1. Top-level `dealId == X` (legacy single-deal portals).
 * 2. The deal's `leadId` -> `clientPortals` row with `leadId == X` (multi-project case).
 * Returns the portal id or `null` if no portal is associated with the deal yet.
 */
export async function findPortalIdForDeal(dealId: string): Promise<string | null> {
  if (!dealId) return null;
  try {
    const byDeal = await getDocs(
      query(collection(db, 'clientPortals'), where('dealId', '==', dealId)),
    );
    if (!byDeal.empty) return byDeal.docs[0].id;
  } catch {
    /* fall through */
  }
  let leadId: string | null = null;
  try {
    const enhanced = await getDoc(doc(db, 'enhancedDeals', dealId));
    if (enhanced.exists()) leadId = (enhanced.data().leadId as string) || null;
    if (!leadId) {
      const legacy = await getDoc(doc(db, 'deals', dealId));
      if (legacy.exists()) leadId = (legacy.data().leadId as string) || null;
    }
  } catch {
    leadId = null;
  }
  if (!leadId) return null;
  try {
    const byLead = await getDocs(
      query(collection(db, 'clientPortals'), where('leadId', '==', leadId)),
    );
    for (const d of byLead.docs) {
      const data = d.data() as { dealId?: string; projects?: Record<string, unknown> };
      if (data.dealId === dealId) return d.id;
      if (data.projects && Object.prototype.hasOwnProperty.call(data.projects, dealId)) {
        return d.id;
      }
    }
    if (!byLead.empty) return byLead.docs[0].id;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Callable wrapper for the `releaseProjectFileToPortal` Cloud Function. Throws if the
 * source file's stream kind is not releasable, or if the caller is not employee/admin.
 */
export async function releaseProjectFileToPortal(
  input: ReleaseProjectFileToPortalInput,
): Promise<ReleaseProjectFileToPortalResult> {
  const fns = getFunctions(undefined, 'us-central1');
  const fn = httpsCallable<ReleaseProjectFileToPortalInput, ReleaseProjectFileToPortalResult>(
    fns,
    'releaseProjectFileToPortal',
  );
  const res = await fn(input);
  return res.data;
}

export type UnreleaseProjectFileFromPortalInput = {
  portalId: string;
  sourceFileId: string;
};

export type UnreleaseProjectFileFromPortalResult = {
  removedReleaseIds: string[];
  removedBundleIds: string[];
};

/**
 * Withdraw all portal releases tied to a Project Hub file version. Removes release
 * history from the client portal and reconciles active deliverable URLs server-side.
 */
export async function unreleaseProjectFileFromPortal(
  input: UnreleaseProjectFileFromPortalInput,
): Promise<UnreleaseProjectFileFromPortalResult> {
  const fns = getFunctions(undefined, 'us-central1');
  const fn = httpsCallable<
    UnreleaseProjectFileFromPortalInput,
    UnreleaseProjectFileFromPortalResult
  >(fns, 'unreleaseProjectFileFromPortal');
  const res = await fn(input);
  return res.data;
}

/** Refresh portal design-approval trail grouping from live Files-tab streams. */
export async function syncPortalApprovalTrailGrouping(input: {
  portalId: string;
  dealId: string;
  projectId?: string;
}): Promise<{ success: boolean; groups?: string[] }> {
  const fns = getFunctions(undefined, 'us-central1');
  const fn = httpsCallable<
    { portalId: string; dealId: string; projectId?: string },
    { success: boolean; groups?: string[] }
  >(fns, 'syncPortalApprovalTrailGrouping');
  const res = await fn(input);
  return res.data;
}

/**
 * Point an already-published client release at the structure track this file
 * lives on now. The public portal reads the structure saved at release time, so
 * a move in Files has to update that copy or the old group keeps the document.
 * A package that still contains other structures lets this file go on its own.
 */
export async function followPortalReleaseScope(
  sourceFileId: string,
  stream: Pick<ProjectFileStream, 'id' | 'title' | 'structureIds' | 'structureNames'>,
): Promise<void> {
  const fileId = (sourceFileId || '').trim();
  const streamId = (stream.id || '').trim();
  if (!fileId || !streamId) return;
  // Designers/engineers/architects are denied the collection-group rule; callers
  // that still hit this path fail softly in the catch below.
  const structureIds = (stream.structureIds ?? []).map(id => (id || '').trim()).filter(Boolean);
  const structureNames = structureIds.map((id, index) => (stream.structureNames?.[index] || '').trim() || id);
  try {
    const snap = await getDocs(query(collectionGroup(db, SUBCOLLECTION), where('sourceFileId', '==', fileId)));
    for (const releaseDoc of snap.docs) {
      const data = releaseDoc.data();
      await updateDoc(releaseDoc.ref, {
        structureIds,
        structureNames,
        sourceStreamId: streamId,
        sourceStreamTitle: (stream.title || '').trim() || String(data.sourceStreamTitle || ''),
      });
      const bundleId = typeof data.bundleId === 'string' ? data.bundleId.trim() : '';
      const portalRef = releaseDoc.ref.parent.parent;
      if (!bundleId || !portalRef) continue;
      const bundleRef = doc(portalRef, BUNDLE_SUBCOLLECTION, bundleId);
      const bundleSnap = await getDoc(bundleRef);
      if (!bundleSnap.exists()) {
        await updateDoc(releaseDoc.ref, { bundleId: deleteField() });
        continue;
      }
      const bundle = bundleSnap.data()!;
      const siblingIds = (Array.isArray(bundle.releaseIds) ? bundle.releaseIds : []).map(id => String(id || '')).filter(Boolean);
      const others = siblingIds.filter(id => id !== releaseDoc.id);
      if (others.length === 0) {
        await updateDoc(bundleRef, { structureIds, structureNames });
        continue;
      }
      const siblingSnaps = await Promise.all(others.map(id => getDoc(doc(releaseDoc.ref.parent, id))));
      const nextKey = structureSetKey(structureIds);
      const sameSet = siblingSnaps.every(sibling => {
        if (!sibling.exists()) return true;
        const ids = Array.isArray(sibling.data()?.structureIds)
          ? (sibling.data()!.structureIds as unknown[]).map(id => String(id || '')).filter(Boolean)
          : [];
        return structureSetKey(ids) === nextKey;
      });
      if (sameSet) {
        await updateDoc(bundleRef, { structureIds, structureNames });
        continue;
      }
      const nextIds = siblingIds.filter(id => id !== releaseDoc.id);
      const primary = nextIds.includes(String(bundle.primaryReleaseId || '')) ? bundle.primaryReleaseId : nextIds[0];
      await updateDoc(bundleRef, {
        releaseIds: nextIds,
        primaryReleaseId: primary,
      });
      await updateDoc(releaseDoc.ref, { bundleId: deleteField() });
    }
  } catch (err) {
    console.warn('[portalFileReleaseService] could not move the client portal copy onto the new structure', err);
  }
}

/** Attach or clear a Loom walkthrough on an existing 3D model release. */
export async function setPortalReleaseLoomUrl(
  portalId: string,
  releaseId: string,
  loomUrl: string,
): Promise<void> {
  const trimmed = loomUrl.trim();
  await updateDoc(doc(db, 'clientPortals', portalId, SUBCOLLECTION, releaseId), {
    loomUrl: trimmed || deleteField(),
  });
}
